import { isAxiosError } from "axios";
import axios from "../config/axios";
import { ApiProjectSettings, ApiPositionSettings } from "../shared/interfaces";

interface AssetUploadResponse {
    file_id: string;
    asset_type: string;
    filename: string;
    message: string;
}

class ProjectSettingsApiClass {
    private baseUrl = "/api/ai/project-settings";

    async getAllSettings(prefix?: string): Promise<ApiProjectSettings> {
        const params = prefix ? { prefix } : {};
        const response = await axios.get(this.baseUrl, { params });
        return response.data;
    }

    /** Only the supported shell keys; never loads arbitrary tenant KV values. */
    async getShellSettings(): Promise<ApiProjectSettings> {
        const response = await axios.get(this.baseUrl, { params: { view: "shell" } });
        return response.data;
    }

    async getSetting(key: string): Promise<any> {
        try {
            const response = await axios.get(`${this.baseUrl}/${key}`);
            return response.data.value;
        } catch (err) {
            if (isAxiosError(err) && err.response?.status === 404) return null;
            throw err;
        }
    }

    async updateSetting(key: string, value: any): Promise<void> {
        await axios.put(`${this.baseUrl}/${key}`, { value });
    }

    /** Write many settings in one request — bulk flag toggles, not a per-key PUT loop. */
    async updateSettingsBatch(settings: { key: string; value: any }[]): Promise<void> {
        await axios.put(`${this.baseUrl}/batch`, { settings });
    }

    async deleteSetting(key: string): Promise<void> {
        await axios.delete(`${this.baseUrl}/${key}`);
    }

    async uploadAsset(assetType: "background" | "logo", file: File): Promise<AssetUploadResponse> {
        const formData = new FormData();
        formData.append("file", file);

        const response = await axios.post(`${this.baseUrl}/upload/${assetType}`, formData);
        return response.data;
    }

    async getAssetUrl(assetType: "background" | "logo"): Promise<string | null> {
        try {
            const fileId = await this.getSetting(`project.${assetType}.file_id`);
            if (fileId) {
                const response = await axios.get(`/api/ai/file/${fileId}`);
                return response.data.fileUrl;
            }
        } catch {
            // No asset set
        }
        return null;
    }

    async getAssetSettings(assetType: "background" | "logo"): Promise<ApiPositionSettings | null> {
        try {
            return await this.getSetting(`project.${assetType}.position`);
        } catch {
            return null;
        }
    }

    async getBackgroundUrl(): Promise<string | null> {
        return this.getAssetUrl("background");
    }

    async getLogoUrl(): Promise<string | null> {
        return this.getAssetUrl("logo");
    }

    async getBackgroundSettings(): Promise<ApiPositionSettings | null> {
        return this.getAssetSettings("background");
    }

    async getLogoSettings(): Promise<ApiPositionSettings | null> {
        return this.getAssetSettings("logo");
    }

    async getLastChangedBy(
        assetType: "background" | "logo",
    ): Promise<{ name: string; timestamp: string } | null> {
        try {
            const response = await axios.get(`${this.baseUrl}/last-changed/${assetType}`);
            return response.data.last_changed_by;
        } catch {
            return null;
        }
    }

    // Scheduling helpers
    async refreshSchedules(): Promise<void> {
        await axios.post(`${this.baseUrl}/schedules/refresh`);
    }

    async triggerJob(jobName: string): Promise<{ job_id: number; job_name: string }> {
        const response = await axios.post(`${this.baseUrl}/schedules/${jobName}/trigger`);
        return response.data;
    }
}

export const ProjectSettingsApi = new ProjectSettingsApiClass();
